import { Box, Button, Heading, Icon, Modal, Paragraph } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconErrorFilled } from '@zillow/constellation-icons';
import { shimmerStyles } from '@zg-rentals/loading-components';
import { Cell, Pie, PieChart } from 'recharts';

import { ListingScoreConstants, ZRAMListingScoreConstants } from '../../constants/ListingScoreConstants';
import { memo } from 'react';
import type React from 'react';
// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
import { styled } from '@/styled-system/jsx';

const StyledDiv: React.FC<{ children: React.ReactNode; isInModal?: boolean }> = ({ children, isInModal = false }) => (
  <Box
    css={{
      '& svg': {
        width: isInModal ? '96px' : '38.5px',
        height: isInModal ? '96px' : '38.5px',
      },
    }}
  >
    {children}
  </Box>
);

const StyledHR: React.FC = () => (
  <Box
    css={{
      flexGrow: 0,
      marginBottom: '15px',
      borderBottom: '1px solid',
      borderColor:
        'gray.300' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ambiguous semantic-token suggestion, not migrating in a lint opt-in */,
      borderRight: 'none',
    }}
  />
);

// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
export const StyledListingQualityScore = styled(Box, {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '8px',
    borderRadius: '50%',
    fontWeight: '700',
    flexShrink: 0,
    backgroundColor: 'backgroundDark',
    color: 'white',
  },
  variants: {
    isInModal: {
      true: {
        base: {
          fontSize: 'heading-md',
          height: '96px',
          width: '96px',
        },
      },
      false: {
        base: {
          fontSize: '16px',
          height: '48px',
          width: '48px',
        },
      },
    },
    rating: {
      outstanding: {
        base: {
          backgroundColor: 'green.600',
          color: 'green.100',
        },
      },
      good: {
        base: {
          backgroundColor: 'green.600',
          color: 'green.100',
        },
      },
      needsImprovement: {
        base: {
          backgroundColor: 'yellow.600',
          color: 'yellow.100',
        },
      },
      missingCriticalDetails: {
        base: {
          backgroundColor: 'orange.600',
          color: 'orange.100',
        },
      },
      incomplete: {
        base: {
          backgroundColor: 'red.700',
          color: 'red.100',
        },
      },
    },
  },
});

export type CompletenessScoreType = {
  rating: keyof typeof ListingScoreConstants | keyof typeof ZRAMListingScoreConstants;
  score: number;
};

type Props = {
  onClose: () => void;
  isRecsComplete: boolean;
  completeness: CompletenessScoreType;
  isFeed?: boolean;
  isMultifamily?: boolean;
};

type BodyProps = {
  completeness: CompletenessScoreType;
  isFeed: boolean;
  isRecsComplete: boolean;
  ratingText: string;
  modalHeadline: string;
  isMultifamily?: boolean;
};

type ScorePieChartProps = {
  isRecsComplete: boolean;
  isInModal?: boolean;
  completeness: CompletenessScoreType;
  customPieChartSize?: number;
  isLoading?: boolean;
  isMultifamily?: boolean;
};

type RatingType = React.ComponentProps<typeof StyledListingQualityScore>['rating'];

const ScorePieChartComponent = ({
  isRecsComplete,
  isInModal = false,
  customPieChartSize = 55,
  completeness,
  isLoading = false,
  isMultifamily = false,
}: ScorePieChartProps) => {
  const getPieChartSize = () => {
    if (isInModal) {
      return 125;
    }
    return customPieChartSize;
  };

  const pieChartSize = getPieChartSize();

  if (isLoading || !completeness) {
    return (
      <Box
        css={{
          borderRadius: 'shape.ellipse',
          ...shimmerStyles,
        }}
        style={{
          width: pieChartSize,
          height: pieChartSize,
        }}
        data-loading={true}
      />
    );
  }

  if (isMultifamily) {
    return (
      <StyledListingQualityScore isInModal={isInModal} rating={completeness.rating as RatingType}>
        {completeness.rating === 'incomplete' ? (
          <Icon css={{ color: 'white', fontSize: '16px' }}>
            <IconErrorFilled />
          </Icon>
        ) : (
          `${completeness.score}%`
        )}
      </StyledListingQualityScore>
    );
  }

  const dataForPieChart = [
    { name: completeness?.rating, fillGray: true, percent: 100 - completeness?.score },
    { name: completeness?.rating, percent: completeness?.score },
  ];

  if (isRecsComplete) {
    return (
      <StyledDiv isInModal={isInModal}>
        <IconCheckmarkCircleFilled style={{ color: 'green.400' }} />
      </StyledDiv>
    );
  }

  return (
    <PieChart width={pieChartSize} height={pieChartSize} margin={{ top: 1, left: -1, right: -1, bottom: -1 }}>
      <Pie data={dataForPieChart} isAnimationActive={false} dataKey="percent" startAngle={90} endAngle={449}>
        {dataForPieChart.map((entry, index) => (
          <Cell
            key={`cell-${index}`}
            fill={
              entry.fillGray
                ? '#D1D1D5'
                : ListingScoreConstants[entry.name as keyof typeof ListingScoreConstants].pieChartColor
            }
          />
        ))}
      </Pie>
    </PieChart>
  );
};

export const ScorePieChart = memo(ScorePieChartComponent);

const ScoreDetailsModalContent = ({
  completeness,
  isFeed,
  isRecsComplete,
  ratingText,
  modalHeadline,
  isMultifamily,
}: BodyProps) => {
  const ratingPrefix =
    isMultifamily && completeness.rating in ZRAMListingScoreConstants
      ? ZRAMListingScoreConstants[completeness.rating as keyof typeof ZRAMListingScoreConstants]?.ratingPrefix
      : null;

  return (
    <>
      <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
        <Paragraph>{ratingPrefix ? ratingPrefix : "Your listing's completeness"}</Paragraph>
        <Heading level={3} css={{ marginBottom: 'sm' }} fontFamily="heading-alt">
          {ratingText.charAt(0).toUpperCase() + ratingText.slice(1)}
        </Heading>
        <ScorePieChart
          isRecsComplete={isRecsComplete}
          isInModal={true}
          completeness={completeness}
          isMultifamily={isMultifamily}
        />
      </Box>
      <Box css={{ marginTop: 'md', marginBottom: 'md' }}>
        <StyledHR />
      </Box>
      <Paragraph textStyle="body-sm-bold">
        {isRecsComplete && !isMultifamily
          ? 'Your listing is outstanding! You have done everything to make it complete.'
          : modalHeadline}
      </Paragraph>
      <Paragraph css={{ marginTop: 'sm', marginBottom: 'sm' }}>
        {isMultifamily
          ? 'Renters want to know key details about your property before they reach out. Adding missing information such as photos, lease terms, parking, and unit availability helps renters decide if your property is a good fit.'
          : 'Renters want to know details about your property before they reach out. Providing these details helps reduce back-and-forth on details like pet policy, lease terms, and your availability.'}
      </Paragraph>
      <Paragraph css={{ marginBottom: 'sm' }}>
        {isMultifamily
          ? 'Your listing quality score reflects property detail completeness and unit-level availability, weighted by their importance to renters using Zillow internal data and market insights. Lead-impact estimates are modeled predictions based on Zillow internal analysis of historical and real-time performance of similar listings. Actual results vary due to market conditions, seasonality, ad spend, and other factors. Estimates are for informational purposes only and do not guarantee leads, leases, or revenue.'
          : 'Zillow calculates your listing completeness by assessing what is missing from your listing and scoring each item based on its importance to renters.'}
      </Paragraph>
      {!isRecsComplete && (
        <Paragraph>
          {isFeed && (
            <Paragraph textStyle="body-sm-bold">
              You can add missing property attributes to your listing within your feed.&nbsp;
            </Paragraph>
          )}
          {!isMultifamily &&
            'Follow your personalized recommendations to boost your listing completeness and stand out to renters.'}
        </Paragraph>
      )}
    </>
  );
};

const ScoreDetailsModalComponent = ({
  completeness,
  isFeed = false,
  isRecsComplete,
  onClose,
  isMultifamily,
}: Props) => {
  const { ratingText, modalHeadline } =
    isMultifamily && completeness.rating in ZRAMListingScoreConstants
      ? ZRAMListingScoreConstants[completeness.rating as keyof typeof ZRAMListingScoreConstants]
      : ListingScoreConstants[completeness.rating as keyof typeof ListingScoreConstants];

  return (
    <Modal
      data-testid="ModalWrapper"
      open={true}
      dividers={false}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      size={{ base: 'fullScreen', md: 'md' }}
      body={
        <ScoreDetailsModalContent
          completeness={completeness}
          isFeed={isFeed}
          ratingText={ratingText}
          modalHeadline={modalHeadline}
          isRecsComplete={isRecsComplete}
          isMultifamily={isMultifamily}
        />
      }
      footer={
        <Button data-testid="submit-button" tone="brand" emphasis="filled" onClick={onClose}>
          Done
        </Button>
      }
    />
  );
};

export const ScoreDetailsModal = memo(ScoreDetailsModalComponent);
